import { invalidate, useFrame } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import { Color, Quaternion, Vector3 } from 'three';

import {
    getEffectiveDistanceSq,
    getLodLevelByDistanceSqWithHysteresis
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleLodUtils';
import { removeCachedInstanceEdges } from 'app/modules/wallforming/components/scenes/objects/articles/utils/edgesOverlayUtils';
import { getDimensionHiddenSheetArticles } from 'app/modules/wallforming/components/scenes/objects/articles/utils/sheetVisibilityUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

interface CameraLodSyncHandlerProps {
    isMainCanvas: boolean;
}

const UPDATE_INTERVAL_SECONDS = 0.08;
const EDGE_REBUILD_IDLE_SECONDS = 0.12;
const EDGE_REBUILD_MIN_INTERVAL_SECONDS = 0.12;
const EDGE_REBUILD_MAX_DEFER_SECONDS = 0.5;
const POSITION_DELTA_EPSILON_SQ = 0.0001;
const ZOOM_DELTA_EPSILON = 0.0001;
const ROTATION_DOT_EPSILON = 0.00001;
const DEBUG_LOD_COLORS = ['true', '1', 'yes', 'on'].includes(
    String(import.meta.env.VITE_DEBUG_LOD_COLORS ?? '')
        .trim()
        .toLowerCase()
);

const lodColors = [new Color('#08c91b'), new Color('#ad213d'), new Color('#1009e0')];

/**
 * Camera-driven LOD swap for batched physical instances.
 * Uses already-registered LOD geometry IDs and updates only when camera moved.
 */
export const useCameraLodSyncHandler = ({ isMainCanvas }: CameraLodSyncHandlerProps) => {
    const lastCameraPositionRef = useRef<Vector3>(new Vector3(Number.NaN, Number.NaN, Number.NaN));
    const lastCameraQuaternionRef = useRef<Quaternion>(new Quaternion(Number.NaN, Number.NaN, Number.NaN, Number.NaN));
    const lastCameraZoomRef = useRef<number>(Number.NaN);
    const lastInstanceCountRef = useRef<number>(-1);
    const elapsedRef = useRef<number>(0);
    const pendingEdgesOverlayRefreshRef = useRef<boolean>(false);
    const dirtyEdgeInstanceIdsRef = useRef<Set<string>>(new Set());
    const changedInstanceIdsRef = useRef<Set<string>>(new Set());
    const cameraStillElapsedRef = useRef<number>(0);
    const edgeRefreshElapsedRef = useRef<number>(0);

    const appliedPhysicalLodRef = useRef<Map<number, number | null>>(new Map());
    const appliedSheetLodRef = useRef<Map<number, number | null>>(new Map());
    const appliedArticleLodRef = useRef<Map<string, number>>(new Map());
    const lockedInstanceIds = useRef<Set<string>>(new Set()).current;

    useEffect(() => {
        appliedPhysicalLodRef.current.clear();
        appliedSheetLodRef.current.clear();
        appliedArticleLodRef.current.clear();
        lastCameraPositionRef.current.set(Number.NaN, Number.NaN, Number.NaN);
        lastCameraQuaternionRef.current.set(Number.NaN, Number.NaN, Number.NaN, Number.NaN);
        lastCameraZoomRef.current = Number.NaN;
        lastInstanceCountRef.current = -1;
        pendingEdgesOverlayRefreshRef.current = false;
        dirtyEdgeInstanceIdsRef.current.clear();
        changedInstanceIdsRef.current.clear();
        cameraStillElapsedRef.current = 0;
        edgeRefreshElapsedRef.current = 0;
    }, [isMainCanvas]);

    const collectDirtyEdgeInstances = (
        state: ReturnType<typeof usePlacementStore.getState>,
        changedInstanceIds: Set<string>
    ) => {
        const isEdgeOverlayActive =
            isMainCanvas && state.graphicsSettings.edgesOverlayEnabled && state.graphicsSettings.aoQuality === 'off';

        if (!isEdgeOverlayActive || changedInstanceIds.size === 0) {
            return;
        }

        const dirtySet = dirtyEdgeInstanceIdsRef.current;
        const wasPending = pendingEdgesOverlayRefreshRef.current;
        for (const instanceId of changedInstanceIds) {
            dirtySet.add(instanceId);
        }
        pendingEdgesOverlayRefreshRef.current = true;
        if (!wasPending) {
            edgeRefreshElapsedRef.current = 0;
        }
    };

    const tryRefreshEdgeOverlay = (
        state: ReturnType<typeof usePlacementStore.getState>,
        hasCameraChanged: boolean
    ) => {
        const isEdgeOverlayActive =
            isMainCanvas && state.graphicsSettings.edgesOverlayEnabled && state.graphicsSettings.aoQuality === 'off';

        if (!isEdgeOverlayActive || !pendingEdgesOverlayRefreshRef.current) {
            return;
        }

        const canRefreshEdges =
            !state.isDraggingActive() &&
            edgeRefreshElapsedRef.current >= EDGE_REBUILD_MIN_INTERVAL_SECONDS &&
            (!hasCameraChanged
                ? cameraStillElapsedRef.current >= EDGE_REBUILD_IDLE_SECONDS
                : edgeRefreshElapsedRef.current >= EDGE_REBUILD_MAX_DEFER_SECONDS);

        if (!canRefreshEdges) {
            return;
        }

        const dirtyIds = Array.from(dirtyEdgeInstanceIdsRef.current);
        if (dirtyIds.length > 0) {
            removeCachedInstanceEdges(dirtyIds);
        }
        state.updateArticleEdgesOverlay(appliedArticleLodRef.current);
        invalidate();
        pendingEdgesOverlayRefreshRef.current = false;
        dirtyEdgeInstanceIdsRef.current.clear();
        edgeRefreshElapsedRef.current = 0;
    };

    useFrame((_, delta) => {
        elapsedRef.current += delta;
        if (elapsedRef.current < UPDATE_INTERVAL_SECONDS) {
            // In demand-mode frameloop, keep ticking until we reach our tick interval
            if (pendingEdgesOverlayRefreshRef.current) {
                invalidate();
            }
            return;
        }
        const tickSeconds = elapsedRef.current;
        elapsedRef.current = 0;

        const state = usePlacementStore.getState();
        const shouldHideSheets =
            isMainCanvas && state.isPanelQuickReplacementOpen && state.isPanelQuickReplacementGridActive;
        const dimensionHiddenSheetArticles = isMainCanvas ? getDimensionHiddenSheetArticles(state) : undefined;
        const camera = state.mainCameraControlsRef.current?.camera;
        const physicalBatch = isMainCanvas ? state.batchedMeshes.physical : state.navigatorBatchedMeshes.physical;
        const sheetBatch = isMainCanvas ? state.batchedMeshes.sheet : state.navigatorBatchedMeshes.sheet;
        const instanceBatchIds = isMainCanvas ? state.instanceBatchIds : state.navigatorInstanceBatchIds;
        const geometrySlots = isMainCanvas ? state.articleGeometrySlots : state.navigatorGeometrySlots;
        if (!camera || !physicalBatch) {
            return;
        }
        const geometrySetter = physicalBatch as unknown as {
            setGeometryIdAt?: (instanceId: number, geometryId: number) => void;
            setVisibleAt?: (instanceId: number, value: boolean) => void;
        };
        if (typeof geometrySetter.setGeometryIdAt !== 'function') {
            return;
        }

        const cameraPosition = camera.position;
        const cameraQuaternion = camera.quaternion;
        const cameraZoom = camera.zoom;
        const lastCameraPosition = lastCameraPositionRef.current;
        const lastCameraQuaternion = lastCameraQuaternionRef.current;
        const currentInstanceCount = instanceBatchIds.size;
        const hasInstanceCountChanged = lastInstanceCountRef.current !== currentInstanceCount;
        lastInstanceCountRef.current = currentInstanceCount;

        const movedDistanceSq = Number.isNaN(lastCameraPosition.x)
            ? Number.POSITIVE_INFINITY
            : lastCameraPosition.distanceToSquared(cameraPosition);
        const zoomChanged = Number.isNaN(lastCameraZoomRef.current)
            ? true
            : Math.abs(lastCameraZoomRef.current - cameraZoom) > ZOOM_DELTA_EPSILON;
        const rotationChanged = Number.isNaN(lastCameraQuaternion.x)
            ? true
            : 1 - Math.abs(lastCameraQuaternion.dot(cameraQuaternion)) > ROTATION_DOT_EPSILON;
        const hasCameraChanged = movedDistanceSq >= POSITION_DELTA_EPSILON_SQ || zoomChanged || rotationChanged;
        if (hasCameraChanged) {
            cameraStillElapsedRef.current = 0;
        } else {
            cameraStillElapsedRef.current += tickSeconds;
        }
        edgeRefreshElapsedRef.current += tickSeconds;

        // Lock LOD update for dragged instances
        const draggingActive = state.isDraggingActive?.() ?? false;
        const selectedInstanceIds = state.articleInstanceSelections ? Object.keys(state.articleInstanceSelections) : [];
        lockedInstanceIds.clear();

        if (draggingActive && selectedInstanceIds.length > 0) {
            for (const id of selectedInstanceIds) {
                lockedInstanceIds.add(id);
            }
        }

        if (
            !DEBUG_LOD_COLORS &&
            !hasInstanceCountChanged &&
            !hasCameraChanged &&
            !pendingEdgesOverlayRefreshRef.current
        ) {
            return;
        }

        // Clear cacher when instances are added
        if (hasInstanceCountChanged) {
            appliedPhysicalLodRef.current.clear();
            appliedSheetLodRef.current.clear();
            appliedArticleLodRef.current.clear();
        }

        lastCameraPosition.copy(cameraPosition);
        lastCameraQuaternion.copy(cameraQuaternion);
        lastCameraZoomRef.current = cameraZoom;

        let anyChanged = false;
        const changedInstanceIds = changedInstanceIdsRef.current;
        changedInstanceIds.clear();

        for (const [articleInstanceId, batchIds] of instanceBatchIds.entries()) {
            if (!batchIds.physicalIds || batchIds.physicalIds.length === 0) {
                continue;
            }
            const slots = geometrySlots.get(batchIds.articleId);
            if (!slots || slots.physicalSlots.length === 0) {
                continue;
            }
            const instance = state.articles[batchIds.articleId]?.articleInstances[articleInstanceId];
            if (!instance) {
                continue;
            }
            // Lock LOD update for dragged instances
            if (lockedInstanceIds.has(articleInstanceId)) {
                continue;
            }
            const firstPhysicalPartName = slots.physicalSlots[0]?.partName;
            const lodPart =
                (firstPhysicalPartName ? instance.parts[firstPhysicalPartName] : undefined) ??
                Object.values(instance.parts)[0];
            if (!lodPart) {
                continue;
            }
            const distanceSq = getEffectiveDistanceSq(camera, cameraPosition, lodPart.position);
            const previousArticleLod = appliedArticleLodRef.current.get(articleInstanceId);
            const smoothedLodLevel = getLodLevelByDistanceSqWithHysteresis(distanceSq, previousArticleLod);
            const lodLevel = isMainCanvas ? smoothedLodLevel : Math.max(1, smoothedLodLevel);
            appliedArticleLodRef.current.set(articleInstanceId, lodLevel);

            for (let i = 0; i < batchIds.physicalIds.length; i++) {
                const physicalInstanceId = batchIds.physicalIds[i];
                const slot = slots.physicalSlots[i];
                if (!slot) {
                    continue;
                }

                const targetGeometryId = slot.lodSlotIds[lodLevel] ?? null;
                const appliedGeometryId = appliedPhysicalLodRef.current.get(physicalInstanceId);
                const hasTargetLod = typeof targetGeometryId === 'number';
                if (appliedGeometryId !== targetGeometryId) {
                    if (hasTargetLod) {
                        geometrySetter.setGeometryIdAt(physicalInstanceId, targetGeometryId);
                    }
                    // Never hide dragged/hovered instances
                    if (
                        typeof geometrySetter.setVisibleAt === 'function' &&
                        !lockedInstanceIds.has(articleInstanceId)
                    ) {
                        geometrySetter.setVisibleAt(physicalInstanceId, hasTargetLod);
                    }
                    appliedPhysicalLodRef.current.set(physicalInstanceId, targetGeometryId);
                    anyChanged = true;
                    changedInstanceIds.add(articleInstanceId);
                }
                if (DEBUG_LOD_COLORS && hasTargetLod) {
                    physicalBatch.setColorAt(physicalInstanceId, lodColors[lodLevel]);
                    anyChanged = true;
                }
            }
            if (sheetBatch && batchIds.sheetId !== null && slots.sheetLodSlotIds.length > 0) {
                const sheetGeometrySetter = sheetBatch as unknown as {
                    setGeometryIdAt?: (instanceId: number, geometryId: number) => void;
                    setVisibleAt?: (instanceId: number, value: boolean) => void;
                };

                const targetSheetGeometryId = slots.sheetLodSlotIds[lodLevel] ?? null;
                const appliedSheetGeometryId = appliedSheetLodRef.current.get(batchIds.sheetId);
                const hasTargetSheetLod = typeof targetSheetGeometryId === 'number';

                if (appliedSheetGeometryId !== targetSheetGeometryId) {
                    if (hasTargetSheetLod && typeof sheetGeometrySetter.setGeometryIdAt === 'function') {
                        sheetGeometrySetter.setGeometryIdAt(batchIds.sheetId, targetSheetGeometryId);
                    }

                    if (typeof sheetGeometrySetter.setVisibleAt === 'function') {
                        const hideDimensionSheet =
                            dimensionHiddenSheetArticles?.[batchIds.articleId]?.includes(articleInstanceId) ?? false;
                        sheetGeometrySetter.setVisibleAt(
                            batchIds.sheetId,
                            hasTargetSheetLod && !shouldHideSheets && !hideDimensionSheet
                        );
                    }

                    appliedSheetLodRef.current.set(batchIds.sheetId, targetSheetGeometryId);
                    anyChanged = true;
                    changedInstanceIds.add(articleInstanceId);
                }
            }
        }

        if (anyChanged) {
            collectDirtyEdgeInstances(state, changedInstanceIds);
            invalidate();
        }

        tryRefreshEdgeOverlay(state, hasCameraChanged);

        // In demand-mode frameloop, keep ticking until the pending edge refresh fires
        if (pendingEdgesOverlayRefreshRef.current) {
            invalidate();
        }
    });
};
